import React, { useContext, useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Flex, Form, Row, Col, Typography, Input, theme, Select, Spin, Checkbox, DatePicker, InputNumber, Grid, Tooltip } from 'antd';
import _ from 'lodash';
import dayjs from 'dayjs';

import { MessageContext } from '../../../layouts/NavigationLayout';
import GeneralHelper from '../../../helpers/general';
import Countries from '../../../constants/Countries';

import CommonEnum from '../../../constants/CommonEnum';
import Confirmation from '../../../components/Confirmation';
import ButtonGroupComponent from './ButtonGroupComponent';

const { Title, Text } = Typography;
const { Item } = Form;
const { useToken } = theme;
const { useBreakpoint } = Grid;

const EducationBackgroundView = (props) => {
	const [nextDisabled, setNextDisabled] = useState(true);
	const [draftDisabled, setDraftDisabled] = useState(true);
	const [isNextStep, setIsNextStep] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [postGradRequired, setPostGradRequired] = useState(false);
	const [selectedCurrentYearFirst, setSelectedCurrentYearFirst] = useState(null);
	const [selectedCurrentYearSecond, setSelectedCurrentYearSecond] = useState(null);
	const [isTOEFL, setIsTOEFL] = useState(false);
	const [isIELTS, setIsIELTS] = useState(false);
	const [isEngProfOthers, setIsEngProfOthers] = useState(false);
	const [userEnglishFirstLang, setUserEnglishFirstLang] = useState(false);
	const [institutionEnglishFirstLang, setInstitutionEnglishFirstLang] = useState(false);
	const [confirmation, setConfirmation] = useState(false);

	const history = useHistory();
	const formRef = useRef(null);
	const { token } = useToken();
	const messageApi = useContext(MessageContext);
	const screens = useBreakpoint();

	const query = GeneralHelper.getParamsFromUrl();
	const { application_id: applicationId } = query || {};
	const { colorPrimary } = token;

	const { educationBackgroundPageProps, applicationInfoPageProps, getApplication, getEducationBackgroundByApplicationId, onEducationBackgroundSubmit,
		resetEducationBackgroundPage, resetEducationBackgroundMeta, reviewAndSubmissionPageProps, setHasUnsavedChanges, resetApplicationInfoMeta } = props;
	const { educationBackground, loading, loadingSubmit, meta } = educationBackgroundPageProps;
	const { user_english_first_lang, institution_english_first_lang, ielts_score, toefl_score, english_prof_others_score, english_prof_others } = educationBackground[0] || {};
	const { application } = reviewAndSubmissionPageProps;
	const { meta: applicationInfoMeta } = applicationInfoPageProps;
	const currentStep = _.get(application, 'current_step');

	useEffect(() => {
		if (applicationId) {
			getEducationBackgroundByApplicationId(applicationId);
		} else {
			history.push('/application?step=0');	//	return to step 0 if applicationId not exist
		}
		return () => {
			resetEducationBackgroundPage();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!_.isEmpty(educationBackground)) {
			// Update the values of the checkboxes
			setUserEnglishFirstLang(user_english_first_lang);
			setInstitutionEnglishFirstLang(institution_english_first_lang);
			toefl_score && setIsTOEFL(true);
			ielts_score && setIsIELTS(true);
			english_prof_others_score && setIsEngProfOthers(true);
			english_prof_others && setIsEngProfOthers(true);

			setIsInitialLoad(true);

			if (educationBackground.length === 1) {
				setSelectedCurrentYearFirst(_.isNil(educationBackground[0].current_year) ? null : CommonEnum.year_of_study.some(option => option.value === educationBackground[0].current_year) ? educationBackground[0].current_year : 'others');
			} else if (educationBackground.length === 2) {
				setSelectedCurrentYearFirst(_.isNil(educationBackground[0].current_year) ? null : CommonEnum.year_of_study.some(option => option.value === educationBackground[0].current_year) ? educationBackground[0].current_year : 'others');
				setSelectedCurrentYearSecond(_.isNil(educationBackground[1].current_year) ? null : CommonEnum.year_of_study.some(option => option.value === educationBackground[1].current_year) ? educationBackground[1].current_year : 'others');
			}

		}
		checkFormValues();
		checkPostGraduateValues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [educationBackground]);

	useEffect(() => {
		checkFormValues();
		checkPostGraduateValues();
		onExistingEducBgValClear();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postGradRequired]);

	useEffect(() => {
		if (!_.isNil(meta)) {
			if (meta.code === 200 && isNextStep) {
				if (currentStep === 6) {
					window.location.href = process.env.NODE_ENV === 'development'
						? `/application?step=6&application_id=${applicationId}`
						: `/summercamp/app/application?step=6&application_id=${applicationId}`;
				} else {
					history.push(`/application?step=3&application_id=${applicationId}`);
				}
			} else if (meta.code === 200 && !isNextStep) {
				isInitialLoad === false && messageApi.open({
					type: 'success',
					content: `${meta.message}`,
					duration: 2.5
				});
				!nextDisabled && query.application_id && getApplication(query.application_id);
			} else {
				messageApi.open({
					type: 'error',
					content: `${meta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetEducationBackgroundMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meta]);

	useEffect(() => {
		if (applicationInfoMeta) {
			if (applicationInfoMeta.code === 200) {
				messageApi.open({
					type: 'success',
					content: `${applicationInfoMeta.message}`,
					duration: 2.5
				});
			} else {
				messageApi.open({
					type: 'error',
					content: `${applicationInfoMeta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetApplicationInfoMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationInfoMeta]);

	useEffect(() => {
		checkFormValues();
		checkPostGraduateValues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTOEFL, isIELTS, isEngProfOthers, userEnglishFirstLang, institutionEnglishFirstLang, isInitialLoad]);

	const getInitialValues = () => {
		const initialValues = {};

		if (_.isEmpty(educationBackground)) {
			return initialValues;
		}

		for (const [index, item] of educationBackground.entries()) {
			const {
				type,
				institution,
				country,
				school,
				program,
				mode,
				current_year,
				expected_completion_date,
				current_gpa,
				highest_score,
				toefl_score,
				ielts_score,
				english_prof_others_score,
				english_prof_others
			} = item;
			if (type === 'undergraduate') {
				initialValues['institution_1'] = institution;
				initialValues['country_1'] = country;
				initialValues['school_1'] = school;
				initialValues['program_1'] = program;
				initialValues['mode_1'] = mode;
				initialValues['current_year_1'] = _.isNil(educationBackground[index].current_year) ? null : CommonEnum.year_of_study.some(option => option.value === educationBackground[index].current_year) ? current_year : 'others';
				if (!CommonEnum.year_of_study.some(option => option.value === educationBackground[index].current_year)) {
					initialValues['current_year_input_1'] = current_year;
				}
				initialValues['expected_completion_date_1'] = expected_completion_date ? dayjs(expected_completion_date) : null;
				initialValues['current_gpa_1'] = current_gpa;
				initialValues['highest_score_1'] = highest_score;

				if (english_prof_others)
					initialValues['english_prof_others'] = english_prof_others;
				if (toefl_score && toefl_score !== 'N/A')
					initialValues['score'] = toefl_score;
				if (ielts_score && ielts_score !== 'N/A')
					initialValues['score'] = ielts_score;
				if (english_prof_others_score)
					initialValues['score'] = english_prof_others_score;

			} else if (type === 'postgraduate') {
				initialValues['institution_2'] = institution;
				initialValues['country_2'] = country;
				initialValues['school_2'] = school;
				initialValues['program_2'] = program;
				initialValues['mode_2'] = mode;
				initialValues['current_year_2'] = _.isNil(educationBackground[index].current_year) ? null : CommonEnum.year_of_study.some(option => option.value === educationBackground[index].current_year) ? current_year : 'others';
				if (!CommonEnum.year_of_study.some(option => option.value === educationBackground[index].current_year)) {
					initialValues['current_year_input_2'] = current_year;
				}
				initialValues['expected_completion_date_2'] = expected_completion_date ? dayjs(expected_completion_date) : null;
				initialValues['current_gpa_2'] = current_gpa;
				initialValues['highest_score_2'] = highest_score;
			}
		}
		return initialValues;
	};


	// const onSubmit = () => {
	// 	history.push(`/application?step=3&application_id=${applicationId}`);
	// };
	const checkFormValues = () => {
		if (formRef.current) {
			if (postGradRequired) {
				if (
					formRef.current.getFieldValue('institution_1') &&
					formRef.current.getFieldValue('country_1') &&
					formRef.current.getFieldValue('school_1') &&
					formRef.current.getFieldValue('program_1') &&
					formRef.current.getFieldValue('mode_1') &&
					(formRef.current.getFieldValue('current_year_1') === 'others' ? formRef.current.getFieldValue('current_year_input_1') : formRef.current.getFieldValue('current_year_1')) &&
					(formRef.current.getFieldValue('expected_completion_date_1') || !_.isNil(formRef.current.getFieldValue('expected_completion_date_1'))) &&
					_.isNumber(formRef.current.getFieldValue('current_gpa_1')) &&
					formRef.current.getFieldValue('highest_score_1') &&
					(
						(isTOEFL || isIELTS) || (userEnglishFirstLang || institutionEnglishFirstLang ? true :
							((isEngProfOthers) && (formRef.current.getFieldValue('score') && !_.isEmpty(formRef.current.getFieldValue('score').toString()) ? true : false)) &&
							(isEngProfOthers ? formRef.current.getFieldValue('english_prof_others') : true)
						)
					) &&

					formRef.current.getFieldValue('institution_2') &&
					formRef.current.getFieldValue('country_2') &&
					formRef.current.getFieldValue('school_2') &&
					formRef.current.getFieldValue('program_2') &&
					formRef.current.getFieldValue('mode_2') &&
					(formRef.current.getFieldValue('current_year_2') === 'others' ? formRef.current.getFieldValue('current_year_input_2') : formRef.current.getFieldValue('current_year_2')) &&
					(formRef.current.getFieldValue('expected_completion_date_2') || !_.isNil(formRef.current.getFieldValue('expected_completion_date_2'))) &&
					_.isNumber(formRef.current.getFieldValue('current_gpa_2')) &&
					formRef.current.getFieldValue('highest_score_2')
				) {
					setNextDisabled(false);
				} else {
					setNextDisabled(true);
				}
			} else {
				if (
					formRef.current.getFieldValue('institution_1') &&
					formRef.current.getFieldValue('country_1') &&
					formRef.current.getFieldValue('school_1') &&
					formRef.current.getFieldValue('program_1') &&
					formRef.current.getFieldValue('mode_1') &&
					(formRef.current.getFieldValue('current_year_1') === 'others' ? formRef.current.getFieldValue('current_year_input_1') : formRef.current.getFieldValue('current_year_1')) &&
					(formRef.current.getFieldValue('expected_completion_date_1') || !_.isNil(formRef.current.getFieldValue('expected_completion_date_1'))) &&
					_.isNumber(formRef.current.getFieldValue('current_gpa_1')) &&
					formRef.current.getFieldValue('highest_score_1') &&
					(
						(isTOEFL || isIELTS) || (userEnglishFirstLang || institutionEnglishFirstLang ? true :
							((isEngProfOthers) && (formRef.current.getFieldValue('score') && !_.isEmpty(formRef.current.getFieldValue('score').toString()) ? true : false)) &&
							(isEngProfOthers ? formRef.current.getFieldValue('english_prof_others') : true)
						)
					) &&

					!formRef.current.getFieldValue('institution_2') &&
					!formRef.current.getFieldValue('country_2') &&
					!formRef.current.getFieldValue('school_2') &&
					!formRef.current.getFieldValue('program_2') &&
					!formRef.current.getFieldValue('mode_2') &&
					!formRef.current.getFieldValue('current_year_2') &&
					!formRef.current.getFieldValue('expected_completion_date_2') &&
					!formRef.current.getFieldValue('current_gpa_2') &&
					!formRef.current.getFieldValue('highest_score_2')
				) {
					setNextDisabled(false);
				} else {
					setNextDisabled(true);
				}
			}

			//Separate trappings for draftDisabled
			const currentYearOneIsValid = formRef.current.getFieldValue('current_year_1') === 'others' ?
				(formRef.current.getFieldValue('current_year_input_1') &&
					formRef.current.getFieldValue('current_year_input_1').length > 0 ? true : false) : true;
			const currentYearTwoIsValid = formRef.current.getFieldValue('current_year_2') === 'others' ?
				(formRef.current.getFieldValue('current_year_input_2') &&
					formRef.current.getFieldValue('current_year_input_2').length > 0 ? true : false) : true;

			if (
				currentYearOneIsValid &&
				currentYearTwoIsValid &&
				(
					(formRef.current.getFieldValue('institution_1') && formRef.current.getFieldValue('institution_1').length > 0) ||
					formRef.current.getFieldValue('country_1') ||
					(formRef.current.getFieldValue('school_1') && formRef.current.getFieldValue('school_1').length > 0) ||
					(formRef.current.getFieldValue('program_1') && formRef.current.getFieldValue('program_1').length > 0) ||
					formRef.current.getFieldValue('mode_1') ||
					formRef.current.getFieldValue('current_year_1') ||
					(formRef.current.getFieldValue('expected_completion_date_1') || !_.isNil(formRef.current.getFieldValue('expected_completion_date_1'))) ||
					_.isNumber(formRef.current.getFieldValue('current_gpa_1')) ||
					(formRef.current.getFieldValue('highest_score_1') && formRef.current.getFieldValue('highest_score_1').length > 0) ||
					(
						(isTOEFL || isIELTS) || (userEnglishFirstLang || institutionEnglishFirstLang ? true :
							((isEngProfOthers) && (formRef.current.getFieldValue('score') && !_.isEmpty(formRef.current.getFieldValue('score').toString()) ? true : false)) &&
							(isEngProfOthers ? formRef.current.getFieldValue('english_prof_others') : true)
						)
					) &&

					(formRef.current.getFieldValue('institution_2') && formRef.current.getFieldValue('institution_2').length > 0) ||
					formRef.current.getFieldValue('country_2') ||
					(formRef.current.getFieldValue('school_2') && formRef.current.getFieldValue('school_2').length > 0) ||
					(formRef.current.getFieldValue('program_2') && formRef.current.getFieldValue('program_2').length > 0) ||
					formRef.current.getFieldValue('mode_2') ||
					formRef.current.getFieldValue('current_year_2') ||
					(formRef.current.getFieldValue('expected_completion_date_2') || !_.isNil(formRef.current.getFieldValue('expected_completion_date_2'))) ||
					_.isNumber(formRef.current.getFieldValue('current_gpa_2')) ||
					(formRef.current.getFieldValue('highest_score_2') && formRef.current.getFieldValue('highest_score_2').length > 0)
				)
			) {
				if (isInitialLoad) {
					setDraftDisabled(true);
					setHasUnsavedChanges(false);
				} else {
					setDraftDisabled(false);
					setHasUnsavedChanges(true);
				}

			} else {
				setDraftDisabled(true);
				setHasUnsavedChanges(false);
			}
		}
	};

	const checkPostGraduateValues = () => {
		if (formRef.current) {
			if (
				(formRef.current.getFieldValue('institution_2') && formRef.current.getFieldValue('institution_2').length > 0) ||
				(formRef.current.getFieldValue('country_2') && formRef.current.getFieldValue('country_2').length > 0) ||
				(formRef.current.getFieldValue('school_2') && formRef.current.getFieldValue('school_2').length > 0) ||
				(formRef.current.getFieldValue('program_2') && formRef.current.getFieldValue('program_2').length > 0) ||
				(formRef.current.getFieldValue('mode_2') && formRef.current.getFieldValue('mode_2').length > 0) ||
				formRef.current.getFieldValue('current_year_2') ||
				formRef.current.getFieldValue('expected_completion_date_2') ||
				(_.isNumber(formRef.current.getFieldValue('current_gpa_2')) && `${formRef.current.getFieldValue('current_gpa_2')}`.length > 0) ||
				(formRef.current.getFieldValue('highest_score_2') && formRef.current.getFieldValue('highest_score_2').length > 0)
			) {
				setPostGradRequired(true);
			} else {
				setPostGradRequired(false);
			}
		}
	};

	const handleSubmit = (values) => {
		const fields = [];
		const valuesLength = postGradRequired ? 2 : 1;
		for (let i = 1; i <= valuesLength; i++) {
			const type = i === 1 ? 'undergraduate' : 'postgraduate';
			const field = {
				application_id: query.application_id,
				save_method: nextDisabled ? 'draft_save' : 'final_save',
				type,
				institution: values[`institution_${i}`],
				country: values[`country_${i}`],
				school: values[`school_${i}`],
				program: values[`program_${i}`],
				mode: values[`mode_${i}`],
				current_gpa: values[`current_gpa_${i}`],
				highest_score: values[`highest_score_${i}`],
				isIELTS: isIELTS,
				isTOEFL: isTOEFL
			};
			let currentYearOne = null;
			let currentYearTwo = null;
			let completionDateOne = null;
			let completionDateTwo = null;

			if (i === 1) {
				currentYearOne = selectedCurrentYearFirst ? (selectedCurrentYearFirst !== 'others' ? values[`current_year_${i}`] : values[`current_year_input_${i}`]) : null;
				completionDateOne = values[`expected_completion_date_${i}`] ? dayjs(values[`expected_completion_date_${i}`]).startOf('day') : null;
				if (isTOEFL && values['score']) {
					field.toefl_score = values['score'];
				} else if (isIELTS && values['score']) {
					field.ielts_score = values['score'];
				} else if (isEngProfOthers) {
					field.english_prof_others_score = values['score'];
					field.english_prof_others = values['english_prof_others'];
				}

				field.user_english_first_lang = userEnglishFirstLang;
				field.institution_english_first_lang = institutionEnglishFirstLang;
				if (currentYearOne) field.current_year = currentYearOne;
				if (completionDateOne) field.expected_completion_date = completionDateOne;
			} else if (i === 2) {
				currentYearTwo = selectedCurrentYearSecond ? (selectedCurrentYearSecond !== 'others' ? values[`current_year_${i}`] : values[`current_year_input_${i}`]) : null;
				completionDateTwo = values[`expected_completion_date_${i}`] ? dayjs(values[`expected_completion_date_${i}`]).startOf('day') : null;
				if (currentYearTwo) field.current_year = currentYearTwo;
				if (completionDateTwo) field.expected_completion_date = completionDateTwo;
			}

			if (!_.isEmpty(educationBackground)) {
				const existingEducationBackground = query.application_id && educationBackground.find(educationBg => educationBg.type === type && educationBg.application_id === query.application_id);
				if (existingEducationBackground) {
					field.id = existingEducationBackground._id;
				}
			}
			fields.push(field);
		}

		// return;
		if (!_.isEmpty(fields) && _.isEmpty(educationBackground)) {
			onEducationBackgroundSubmit({ fields });
		} else {
			onEducationBackgroundSubmit({ id: educationBackground[0]._id, fields });
		}
	};

	const onExistingEducBgValClear = () => {
		const existingEducBg = query.application_id && educationBackground.find(educationBg => educationBg.type === 'postgraduate' && educationBg.application_id === query.application_id);

		if (postGradRequired === false && !existingEducBg) {
			clearSecondRefereeValues();
		}
	};

	const clearSecondRefereeValues = () => {
		formRef.current.setFieldsValue({
			institution_2: undefined,
			country_2: undefined,
			school_2: undefined,
			program_2: undefined,
			mode_2: undefined,
			current_year_2: undefined,
			expected_completion_date_2: undefined,
			current_gpa_2: undefined,
			highest_score_2: undefined
		});
	};

	const filterOption = (input, option) =>
		(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

	const renderOptionLabel = (label) => {
		if (screens.xs && label.length > 25) {
			return (
				<Tooltip
					autoAdjustOverflow={true}
					placement="top"
					title={label}
					key={label}
				>
					{GeneralHelper.truncateLongText(label, 25)}
				</Tooltip>
			);
		}
		return label;
	};

	//End of Functions

	if (loading) {
		return (
			<div
				style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<Spin />
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
			<Form
				ref={formRef}
				layout="vertical"
				initialValues={getInitialValues()}
				onFinish={handleSubmit}
				style={{ width: '100%' }}
			>
				<Title
					level={2}
					style={{ color: colorPrimary }}
				>
					Step 3
				</Title>
				<Row justify="space-between">
					<Col
						xl={11}
						lg={11}
						md={24}
						sm={24}
						xs={24}
					>
						<Item>
							<div style={{ marginBottom: 20 }}>
								<Text
									strong
									style={{ fontSize: 20 }}
								>
									Undergraduate Studies
								</Text>
							</div>
						</Item>
						<Item
							label="Name of Institution"
							name="institution_1"
							required
						>
							<Input
								placeholder="Enter Name of Institution"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ institution_1: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Country / Region of Institution"
							name="country_1"
							required
						>
							<Select
								placeholder={!screens.xs ? 'Select Country / Region of Institution' : 'Select Country / Region...'}
								options={Countries.map((country, index) => ({
									value: country.value,
									label: renderOptionLabel(country.label)
								}))}
								showSearch
								filterOption={filterOption}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Faculty / School"
							name="school_1"
							required
						>
							<Input
								placeholder="Enter Faculty / School"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ school_1: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Program of Study (eg. Bachelor of Engineering in Computer Science
								and Engineering)"
							name="program_1"
							required
						>
							<Input
								placeholder="Enter Program of Study"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ program_1: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Mode of Study"
							name="mode_1"
							required
						>
							<Select
								placeholder="Select Mode of Study"
								options={[
									{ value: 'full_time', label: 'Full-Time' },
									{ value: 'part_time', label: 'Part-Time' }
								]}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Current Year of Study"
							name="current_year_1"
							required
						>
							<Select
								placeholder="Select Current Year of Study"
								options={CommonEnum.year_of_study}
								onChange={(value) => {
									if (value === 'others') {
										formRef.current.setFieldsValue({ current_year_input_1: null });
									}
									setIsInitialLoad(false);
									setSelectedCurrentYearFirst(value);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						{
							selectedCurrentYearFirst === 'others' && (
								<Item
									label="Others (Please state)"
									name="current_year_input_1"
									required
								>
									<Input
										placeholder="Enter Current Year of Study"
										onChange={() => {
											setIsInitialLoad(false);
											checkPostGraduateValues();
											checkFormValues();
										}}
										autoComplete="nope"
									/>
								</Item>
							)
						}

						<Item
							label="Expected Date of Completion / Date of Graduation"
							name="expected_completion_date_1"
							required
						>
							<DatePicker
								placeholder="Select Expected Date of Completion / Date of Graduation"
								picker="month"
								style={{ width: '100%' }}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Current GPA / Average Mark"
							name="current_gpa_1"
							required
						>
							<InputNumber
								placeholder="Enter Current GPA / Average Mark"
								onKeyPress={(event) => {
									if (!/[0-9.]/.test(event.key)) {
										event.preventDefault();
									}

									// Allow only one period in the input
									if (event.key === '.' && event.target.value.includes('.')) {
										event.preventDefault();
									}
								}}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								min={0}
								style={{ width: '100%' }}
								controls={false}
								precision={2}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Highest Score in the Grading Scale of Your Institution"
							name="highest_score_1"
							required
						>
							<Input
								placeholder="Enter Highest Score in the Grading Scale of Your Institution"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ highest_score_1: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
					</Col>
					<Col
						xl={11}
						lg={11}
						md={24}
						sm={24}
						xs={24}
					>
						<Item>
							<div style={{ marginBottom: 20 }}>
								<Text
									strong
									style={{ fontSize: 20 }}
								>
									Postgraduate Studies (if applicable)
								</Text>
							</div>
						</Item>
						<Item
							label="Name of Institution"
							name="institution_2"
							required={postGradRequired}
						>
							<Input
								placeholder="Enter Name of Institution"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ institution_2: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Country / Region of Institution"
							name="country_2"
							required={postGradRequired}
						>
							<Select
								placeholder={!screens.xs ? 'Select Country / Region of Institution' : 'Select Country / Region...'}
								options={Countries.map((country, index) => ({
									value: country.value,
									label: renderOptionLabel(country.label)
								}))}
								showSearch
								allowClear
								filterOption={filterOption}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onClear={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Faculty / School"
							name="school_2"
							required={postGradRequired}
						>
							<Input
								placeholder="Enter Faculty / School"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ school_2: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Program of Study (eg. Master of Science in Computer Engineering)"
							name="program_2"
							required={postGradRequired}
						>
							<Input
								placeholder="Enter Program of Study"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ program_2: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Mode of Study"
							name="mode_2"
							required={postGradRequired}
						>
							<Select
								placeholder="Select Mode of Study"
								allowClear
								options={[
									{ value: 'full_time', label: 'Full-Time' },
									{ value: 'part_time', label: 'Part-Time' }
								]}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onClear={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Current Year of Study"
							name="current_year_2"
							required={postGradRequired}
						>
							<Select
								placeholder="Select Current Year of Study"
								options={CommonEnum.year_of_study}
								onChange={(value) => {
									if (value === 'others') {
										formRef.current.setFieldsValue({ current_year_input_2: null });
									}
									setIsInitialLoad(false);
									setSelectedCurrentYearSecond(value);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onClear={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								allowClear
							/>
						</Item>
						{
							selectedCurrentYearSecond === 'others' && (
								<Item
									label="Others (Please state)"
									name="current_year_input_2"
									required
								>
									<Input
										placeholder="Enter Current Year of Study"
										onChange={() => {
											setIsInitialLoad(false);
											checkPostGraduateValues();
											checkFormValues();
										}}
										autoComplete="nope"
									/>
								</Item>
							)
						}
						<Item
							label="Expected Date of Completion / Date of Graduation"
							name="expected_completion_date_2"
							required={postGradRequired}
						>
							<DatePicker
								placeholder="Select Expected Date of Completion / Date of Graduation"
								picker="month"
								style={{ width: '100%' }}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Current GPA / Average Mark"
							name="current_gpa_2"
							required={postGradRequired}
						>
							<InputNumber
								placeholder="Enter Current GPA / Average Mark"
								onKeyPress={(event) => {
									if (!/[0-9.]/.test(event.key)) {
										event.preventDefault();
									}
									// Allow only one period in the input
									if (event.key === '.' && event.target.value.includes('.')) {
										event.preventDefault();
									}
								}}
								onChange={() => {
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								min={0}
								precision={2}
								controls={false}
								style={{ width: '100%' }}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Highest Score in the Grading Scale of Your Institution"
							name="highest_score_2"
							required={postGradRequired}
						>
							<Input
								placeholder="Enter Highest Score in the Grading Scale of Your Institution"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ highest_score_2: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkPostGraduateValues();
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item style={{ marginTop: 66, marginBottom: 10 }}>
							<div>
								<Text
									strong
									style={{ fontSize: 20 }}
								>
									English Proficiency
								</Text>
							</div>
							<div style={{ marginTop: '-7px' }}>
								<Text>
									(Please check the box if appropriate)
								</Text>
							</div>
						</Item>
						<Flex
							wrap="reference"
							style={{ marginBottom: 10 }}
						>
							<div>
								<Checkbox
									onChange={(event) => {
										setIsTOEFL(prevVal => !prevVal);
										!event.target.checked && formRef.current.setFieldsValue({ score: null });
										checkFormValues();
										checkPostGraduateValues();
									}}
									checked={isTOEFL}
									disabled={isIELTS || isEngProfOthers}
									style={{ fontSize: 14 }}
								>
									TOEFL (Paper-based Test)
								</Checkbox>
							</div>
							<div style={{ height: '100%', marginRight: 10 }}>
								<Text>/</Text>
							</div>
							<div>
								<Checkbox
									onChange={(event) => {
										setIsIELTS(prevVal => !prevVal);
										!event.target.checked && formRef.current.setFieldsValue({ score: null });
										checkFormValues();
										checkPostGraduateValues();
									}}
									checked={isIELTS}
									disabled={isTOEFL || isEngProfOthers}
									style={{ fontSize: 14 }}
								>
									IELTS (Academic Module)
								</Checkbox>
							</div>
							<div style={{ height: '100%', marginRight: 10 }}>
								<Text>/</Text>
							</div>
							<div>
								<Checkbox
									onChange={(event) => {
										setIsEngProfOthers(prevVal => !prevVal);
										!event.target.checked && formRef.current.setFieldsValue({ score: null });
										checkFormValues();
										checkPostGraduateValues();
									}}
									checked={isEngProfOthers}
									disabled={isTOEFL || isIELTS}
									style={{ fontSize: 14 }}
								>
									Others
								</Checkbox>
							</div>
						</Flex>
						<Row>
							<Col span={1}>
								<Item>
									<div style={{ paddingTop: 5, paddingRight: '15px' }}></div>
								</Item>
							</Col>
							{/* Only required if either one of them is checked */}
							<Col span={23}>
								{isEngProfOthers &&
									<Item
										name="english_prof_others"
										label={isEngProfOthers ? 'Please state' : ''}
										required={!userEnglishFirstLang && !institutionEnglishFirstLang && isEngProfOthers}
										style={{ marginLeft: 8 }}
									>
										<Input
											type={isEngProfOthers ? 'text' : 'hidden'}
											placeholder="Enter English Proficiency"
											onChange={(e) => {
												const inputValue = e.target.value;

												// Check if it contains disallowed characters
												if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
													// Sanitize the input by removing disallowed characters
													const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

													// Update form with sanitized value
													formRef.current.setFieldsValue({ english_prof_others: sanitizedValue });
												}

												// After sanitizing, proceed with your other operations
												setIsInitialLoad(false);
												checkPostGraduateValues();
												checkFormValues();
											}}
											onKeyDown={GeneralHelper.preventDisallowedKeys}
											autoComplete="nope"
										/>
									</Item>}
								<Item
									name="score"
									label={isTOEFL || isIELTS || isEngProfOthers ? 'Score / Level' : ''}
									// required={!userEnglishFirstLang && !institutionEnglishFirstLang && (isTOEFL || isIELTS || isEngProfOthers)}
									required={!userEnglishFirstLang && !institutionEnglishFirstLang && isEngProfOthers}
									style={{ marginLeft: 8 }}
								>
									<Input
										placeholder="Enter Score / Level"
										disabled={!isIELTS && !isTOEFL && !isEngProfOthers}
										onChange={(e) => {
											const inputValue = e.target.value;

											// Check if it contains disallowed characters
											if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
												// Sanitize the input by removing disallowed characters
												const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

												// Update form with sanitized value
												formRef.current.setFieldsValue({ score: sanitizedValue });
											}

											// After sanitizing, proceed with your other operations
											setIsInitialLoad(false);
											checkPostGraduateValues();
											checkFormValues();
										}}
										onKeyDown={GeneralHelper.preventDisallowedKeys}
										controls={false}
										style={{ width: '100%' }}
										autoComplete="nope"
									/>
								</Item>
								<div style={{ marginLeft: 8 }}>Remark: Applicants are not required to present a TOEFL or IELTS score if they meet one of the following requirements:</div>
							</Col>
						</Row>
						<Row>
							<Col>
								<Flex gap={5}>
									<div>
										<Checkbox
											checked={userEnglishFirstLang}
											onChange={() => {
												setIsInitialLoad(false);
												setUserEnglishFirstLang(!userEnglishFirstLang);
												checkFormValues();
												checkPostGraduateValues();
											}}

										/>
									</div>
									<div>
										<Text>
											Your first language is English
										</Text>
									</div>
								</Flex>
							</Col>
						</Row>
						<Row>
							<Col>
								<Flex gap={5}>
									<div>
										<Checkbox
											checked={institutionEnglishFirstLang}
											onChange={() => {
												setIsInitialLoad(false);
												setInstitutionEnglishFirstLang(!institutionEnglishFirstLang);
												checkFormValues();
												checkPostGraduateValues();
											}}
										/>
									</div>
									<div>
										<Text>
											Your bachelor’s degree (or equivalent qualification) was / will be awarded by an institution where the medium of instruction is English.
										</Text>
									</div>
								</Flex>
							</Col>
						</Row>
					</Col>
				</Row>
				<div style={{ marginTop: 30 }}>
					<ButtonGroupComponent
						currentStep={currentStep}
						draftDisabled={draftDisabled}
						saveDisabled={nextDisabled}
						loadingSubmit={loadingSubmit}
						onSave={() => setIsNextStep(true)}
						onPrevious={() => {
							if (!draftDisabled) {
								setConfirmation(true);
							} else {
								history.push(`/application?step=1&application_id=${applicationId}`);
							}
						}}
					/>
				</div>
				<Confirmation
					loading={loadingSubmit}
					open={confirmation}
					onCancel={() => setConfirmation(false)}
					onOk={() => {
						draftDisabled === false && formRef.current.submit();
						history.push(`/application?step=1&application_id=${applicationId}`);
					}}
				>
					<Flex
						vertical
						gap={30}
					>
						<Text>
							Save your changes before going back to the previous step?
						</Text>
					</Flex>
				</Confirmation>
			</Form>
		</div>
	);
};

export default EducationBackgroundView;
